import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Send, X } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { EditorModal } from "@/components/EditorModal";
import { StaffingRequestForm } from "@/components/StaffingRequestForm";
import { PriorityCell, StatusPill, formatDate } from "@/components/board/boardFormat";
import { api, formatApiError } from "@/lib/api";

const LOCKED_STATUSES = ["Filled", "Assigned", "Confirmed", "In progress", "In Progress", "Completed", "Closed"];

export default function StaffingRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [clientChanges, setClientChanges] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [clientEdit, setClientEdit] = useState(null);
  const [clientChange, setClientChange] = useState(null);
  const [changeText, setChangeText] = useState({ requested_change: "", reason: "" });
  const [error, setError] = useState("");

  const loadRequests = useCallback(async () => {
    try {
      const [response, options, changes] = await Promise.all([
        api.get("/requests"),
        api.get("/requests/client-options"),
        api.get("/requests/change-requests"),
      ]);
      setRequests(response.data);
      setClientOptions(options.data);
      setClientChanges(changes.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  async function submitRequest(requestId) {
    try {
      const response = await api.post(`/requests/${requestId}/transition`, { status: "Submitted", note: "Submitted by client" });
      setRequests(requests.map((item) => (item.id === requestId ? response.data : item)));
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function saveClientEdit(event) {
    event.preventDefault();
    try {
      await api.put(`/requests/${clientEdit.id}/client-update`, {
        role_name: clientEdit.role_name,
        needed_date: clientEdit.needed_date,
        hours: Number(clientEdit.hours),
        priority: clientEdit.priority,
        notes: clientEdit.notes || "",
      });
      setClientEdit(null);
      await loadRequests();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function submitChange(event) {
    event.preventDefault();
    try {
      await api.post(`/requests/${clientChange.id}/change-requests`, changeText);
      setClientChange(null);
      setChangeText({ requested_change: "", reason: "" });
      await loadRequests();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  return (
    <div className="app-shell" data-testid="staffing-requests-shell">
      <AppSidebar activeItem="Staffing Requests" />
      <div className="app-content">
        <AppHeader />
        <main className="dashboard-page requests-page" data-testid="staffing-requests-page">
          <div className="page-intro">
            <div>
              <p className="eyebrow">REQUEST MANAGEMENT</p>
              <h1>Staffing Requests</h1>
              <p className="page-subtitle">Create, submit, and track every professional staffing need for your organization.</p>
            </div>
            <button className="button button-primary" data-testid="new-request-button" type="button" onClick={() => setShowForm(true)}>
              <Plus size={18} /> New staffing request
            </button>
          </div>

          {error && <p className="form-message error-message" data-testid="request-error-message">{error}</p>}
          {showForm && (
            <StaffingRequestForm
              clientOptions={clientOptions}
              onCancel={() => setShowForm(false)}
              onCreated={(request) => { setRequests([request, ...requests]); setShowForm(false); }}
            />
          )}

          <section className="board-table-card" data-testid="staffing-requests-table">
            <div className="section-heading board-card-heading">
              <div>
                <h2>Request portfolio</h2>
                <p data-testid="request-count">{requests.length} requests in view</p>
              </div>
            </div>
            <div className="board-table-scroll">
              <table className="board-table">
                <thead>
                  <tr>
                    <th>Request ID</th>
                    <th>Role</th>
                    <th>Assigned</th>
                    <th>Date needed</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {requests.map((item) => (
                    <tr className="board-row" key={item.id} data-testid={`request-row-${item.id}`}>
                      <td><span className="request-id-link" data-testid={`request-id-${item.id}`}>{item.id}</span></td>
                      <td>{item.role_name}</td>
                      <td data-testid={`request-assigned-${item.id}`}>{item.assigned_person_name || <span className="cell-dash">–</span>}</td>
                      <td>{item.needed_date ? formatDate(item.needed_date) : <span className="cell-dash">–</span>}</td>
                      <td><PriorityCell priority={item.priority} /></td>
                      <td><StatusPill status={item.status} testId={`request-status-${item.id}`} /></td>
                      <td>
                        <div className="table-action-stack">
                          {item.status === "Draft" && (
                            <>
                              <button className="table-edit-button" data-testid={`client-edit-request-${item.id}`} type="button" onClick={() => setClientEdit(item)}><Pencil size={14} /> Edit</button>
                              <button className="table-edit-button table-primary-action" data-testid={`submit-request-${item.id}`} type="button" onClick={() => submitRequest(item.id)}><Send size={14} /> Submit</button>
                            </>
                          )}
                          {item.status !== "Draft" && !LOCKED_STATUSES.includes(item.status) && (
                            <button className="table-edit-button" data-testid={`client-edit-request-${item.id}`} type="button" onClick={() => setClientEdit(item)}><Pencil size={14} /> Edit</button>
                          )}
                          {LOCKED_STATUSES.includes(item.status) && (
                            <button className="table-edit-button" data-testid={`client-change-request-${item.id}`} type="button" onClick={() => setClientChange(item)}>Request change</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!requests.length && <div className="board-empty" data-testid="requests-empty-state">No staffing requests yet. Create the first request to begin.</div>}
            </div>
          </section>

          <section className="client-evaluation-status-panel" data-testid="client-change-request-status">
            <div className="section-heading">
              <div>
                <h2>My change requests</h2>
                <p>TAG reviews changes to active placements before they take effect.</p>
              </div>
            </div>
            {clientChanges.map((item) => (
              <article key={item.id}>
                <strong>{item.status}</strong>
                <p>{item.requested_change}</p>
                {item.feedback && <span>{item.feedback}</span>}
              </article>
            ))}
            {!clientChanges.length && <p className="operations-empty-state">No placement change requests have been submitted.</p>}
          </section>

          {clientEdit && (
            <EditorModal onClose={() => setClientEdit(null)} testId="client-request-editor-modal">
              <section className="inline-edit-panel">
                <div className="section-heading">
                  <div><h2>Edit request</h2><p>{clientEdit.id} · {clientEdit.role_name}</p></div>
                  <button className="projection-icon-button" data-testid="close-client-request-editor" type="button" onClick={() => setClientEdit(null)}><X size={16} /></button>
                </div>
                <form className="inline-edit-form" data-testid="client-request-editor" onSubmit={saveClientEdit}>
                  <label>Role<input value={clientEdit.role_name} onChange={(event) => setClientEdit({ ...clientEdit, role_name: event.target.value })} /></label>
                  <label>Hours<input type="number" value={clientEdit.hours} onChange={(event) => setClientEdit({ ...clientEdit, hours: event.target.value })} /></label>
                  <label>Date needed<input type="date" value={clientEdit.needed_date || ""} onChange={(event) => setClientEdit({ ...clientEdit, needed_date: event.target.value })} /></label>
                  <label>Priority
                    <select value={clientEdit.priority} onChange={(event) => setClientEdit({ ...clientEdit, priority: event.target.value })}>
                      <option>Standard</option><option>High</option><option>Urgent</option>
                    </select>
                  </label>
                  <button className="button button-primary">Save request</button>
                </form>
              </section>
            </EditorModal>
          )}
          {clientChange && (
            <EditorModal onClose={() => setClientChange(null)} testId="client-change-request-modal">
              <section className="inline-edit-panel">
                <div className="section-heading">
                  <div><h2>Request a change</h2><p>{clientChange.id} · {clientChange.role_name}</p></div>
                  <button className="projection-icon-button" data-testid="close-client-change-request" type="button" onClick={() => setClientChange(null)}><X size={16} /></button>
                </div>
                <form className="inline-edit-form" data-testid="client-change-request-form" onSubmit={submitChange}>
                  <label>Requested change<textarea value={changeText.requested_change} onChange={(event) => setChangeText({ ...changeText, requested_change: event.target.value })} /></label>
                  <label>Reason<textarea value={changeText.reason} onChange={(event) => setChangeText({ ...changeText, reason: event.target.value })} /></label>
                  <button className="button button-primary">Submit change request</button>
                </form>
              </section>
            </EditorModal>
          )}
        </main>
      </div>
    </div>
  );
}
